<template>
  <div class="app-container dida-worker-control">
    <dida-module-header
      title="并发与积压控制"
      description="按供应商独立配置全量、近期、中远期和预订失败补推 Worker，并实时观察共享 Redis 队列积压。所有道旅出站推送通道只按 Worker 数控制。"
      :status="moduleStatus"
      :status-text="moduleStatusText"
      :updated-at="backlogSampledAt || updatedAt"
      auto-refresh-text="积压每 10 秒刷新"
      :loading="backlogLoading || configLoading"
      @refresh="refreshDashboard"
    />

    <div class="operations-overview">
      <div class="overview-card overview-card--policy">
        <span class="overview-label">执行策略</span>
        <strong>{{ controlModeText }}</strong>
        <small>QPS 限制：{{ qpsEnabled ? '异常开启' : '未启用' }}</small>
      </div>
      <div class="overview-card">
        <span class="overview-label">独立推送通道</span>
        <strong>{{ channelCount }}</strong>
        <small>全量 / 近期 / 中远期 / 预订失败补推</small>
      </div>
      <div class="overview-card">
        <span class="overview-label">配置 Worker 合计</span>
        <strong>{{ configuredWorkerTotal }}</strong>
        <small>三个通道独立计数</small>
      </div>
      <div class="overview-card" :class="{ 'overview-card--warning': readyBacklogTotal > 0 }">
        <span class="overview-label">当前可处理积压</span>
        <strong>{{ readyBacklogTotal }}</strong>
        <small>处理中 {{ processingTotal }} · 延迟重试 {{ delayedRetryTotal }}</small>
      </div>
    </div>

    <el-alert
      v-if="policyConflict"
      class="policy-alert"
      title="后端执行策略与 Worker-only 生产要求冲突，请停止发布并检查服务配置。"
      type="error"
      :closable="false"
      show-icon
    />
    <el-alert
      v-if="configError"
      class="policy-alert"
      :title="configError"
      type="error"
      :closable="false"
      show-icon
    />
    <el-card shadow="never" class="help-card">
      <div slot="header" class="clearfix">
        <span>Dida Worker 并发控制</span>
        <el-button style="float: right" type="primary" size="small" :loading="saving" @click="save">保存并生效</el-button>
      </div>
      <el-alert
        title="配置保存在共享数据库中，Go 服务会自动读取，无需重启；全量、近期、中远期和预订失败补推通道只受各自 Worker 数控制，不设置 QPS，不按比例换算，也不互相借用。"
        type="info"
        :closable="false"
        show-icon
      />
      <section class="backlog-board">
        <div class="backlog-heading">
          <div>
            <div class="setting-title">推送运行态与积压</div>
            <div class="setting-description">全量展示当前校准轮次的真实进度；近期和中远期展示共享 Redis 差异队列。“可处理”包含当前轮执行中任务；显示 0 仅表示本次采样无积压，不表示没有发生过差异推送。</div>
          </div>
          <div class="backlog-actions">
            <span class="sample-time">采样时间：{{ backlogSampledAt || '-' }}</span>
            <el-button size="mini" :loading="backlogLoading" icon="el-icon-refresh" @click="loadBacklog">刷新</el-button>
          </div>
        </div>
        <el-alert
          v-if="backlogStatus === 'unavailable'"
          :title="backlogMessage || 'Redis 推送积压暂时无法读取，请稍后重试'"
          type="error"
          :closable="false"
          show-icon
        />
        <div class="backlog-grid">
          <div v-for="supplier in suppliers" :key="supplier.value" class="backlog-card">
            <div class="backlog-card-title">
              <span>{{ supplier.label }}</span>
              <el-tag size="mini" :type="supplierBacklogType(supplier.value)">{{ supplierBacklogText(supplier.value) }}</el-tag>
            </div>
            <div class="lane-block lane-block--full" :class="{ 'lane-block--retrying': isFullRetrying(supplier.value) }">
              <div class="lane-title">
                <span>全量校准 <el-tag size="mini" :type="fullStatusTagType(supplier.value)">{{ fullStatusText(fullMetric(supplier.value).status) }}</el-tag></span>
                <strong>{{ fullDisplayProgress(supplier.value) }}%</strong>
              </div>
              <el-progress
                :class="{ 'retry-progress-bar': isFullRetrying(supplier.value) }"
                :percentage="fullDisplayProgress(supplier.value)"
                :status="fullProgressStatus(supplier.value)"
                :color="fullProgressColor(supplier.value)"
                :stroke-width="isFullRetrying(supplier.value) ? 10 : 6"
                :show-text="false"
              />
              <div v-if="isFullRetrying(supplier.value)" class="retry-visual-status" :class="{ 'retry-visual-status--stalled': isFullRetryStalled(supplier.value) }">
                <span class="retry-live-dot" />
                <div>
                  <strong>{{ isFullRetryStalled(supplier.value) ? '重试可能停滞' : (fullMetric(supplier.value).status === 'retry_requested' ? '重试请求已提交' : '失败酒店正在重试') }}</strong>
                  <small>{{ fullRetryStatusText(supplier.value) }}</small>
                </div>
              </div>
              <div v-if="isFullRetrying(supplier.value)" class="lane-tags lane-tags--retry">
                <el-tag size="mini" type="warning" effect="plain">重试总数 {{ fullMetric(supplier.value).retry_total }}</el-tag>
                <el-tag size="mini" type="success" effect="plain">已完成 {{ fullMetric(supplier.value).retry_processed }}</el-tag>
                <el-tag size="mini" type="danger" effect="plain">剩余 {{ fullMetric(supplier.value).retry_remaining }}</el-tag>
              </div>
              <div v-else class="lane-tags">
                <el-tag size="mini" effect="plain">已处理 {{ fullMetric(supplier.value).processed_hotels }}</el-tag>
                <el-tag size="mini" type="danger" effect="plain">当前失败 {{ fullMetric(supplier.value).failed }}</el-tag>
                <el-tag size="mini" type="warning" effect="plain">待重试 {{ fullMetric(supplier.value).retry_hotels }}</el-tag>
                <el-tag v-if="fullMetric(supplier.value).ignored_failures" size="mini" type="info" effect="plain">已忽略 {{ fullMetric(supplier.value).ignored_failures }}</el-tag>
              </div>
              <div v-if="isFullRetrying(supplier.value)" class="lane-meta lane-meta--retry">
                <span>开始时间 {{ fullMetric(supplier.value).retry_started_at || fullMetric(supplier.value).operator_action_requested_at || '-' }}</span>
                <span>最近进展 {{ fullMetric(supplier.value).last_batch_at || '-' }}</span>
                <span class="retry-progress-copy">已完成 {{ fullMetric(supplier.value).retry_processed }}/{{ fullMetric(supplier.value).retry_total }} 家，剩余 {{ fullMetric(supplier.value).retry_remaining }} 家</span>
              </div>
              <div v-else class="lane-meta">
                <span>{{ fullMetric(supplier.value).processed_hotels }}/{{ fullMetric(supplier.value).authorized_hotels }} 家</span>
                <span>最近批次 {{ fullMetric(supplier.value).last_batch_at || '-' }}</span>
                <span v-if="fullMetric(supplier.value).ignored_failures">已忽略 {{ fullMetric(supplier.value).ignored_failures }} · {{ fullMetric(supplier.value).ignored_at || '-' }}</span>
                <span v-if="fullMetric(supplier.value).ignored_by">操作人 ID {{ fullMetric(supplier.value).ignored_by }}</span>
                <span v-if="fullMetric(supplier.value).operator_action">操作提交于 {{ fullMetric(supplier.value).operator_action_requested_at || '-' }}</span>
                <span v-if="fullMetric(supplier.value).ignore_reason" class="lane-failure">忽略原因：{{ fullMetric(supplier.value).ignore_reason }}</span>
              </div>
              <div v-if="hasFullFailureEvidence(supplier.value)" class="full-actions">
                <el-button size="mini" type="danger" icon="el-icon-warning-outline" @click="openFullFailureDetails(supplier.value)">查看失败原因</el-button>
                <el-button v-if="canHandleFullFailure(supplier.value)" size="mini" type="primary" plain :loading="fullActionLoading === supplier.value + ':retry'" @click="retryFullCalibration(supplier.value)">重试失败项</el-button>
                <el-button v-if="canHandleFullFailure(supplier.value)" size="mini" type="warning" plain :loading="fullActionLoading === supplier.value + ':ignore'" @click="ignoreFullCalibration(supplier.value)">忽略本轮异常</el-button>
              </div>
            </div>
            <el-divider class="lane-divider" />
            <div class="lane-block lane-block--realtime">
              <div class="lane-title"><span>近期实时 <el-tag size="mini" :type="laneStatusTagType(supplier.value, 'recent')">{{ laneStatusText(supplier.value, 'recent') }}</el-tag></span><strong>{{ backlogMetric(supplier.value, 'recent').ready }}</strong></div>
              <el-progress :percentage="backlogPercentage(supplier.value, 'recent')" :status="backlogProgressStatus(supplier.value, 'recent')" :show-text="false" />
              <div class="lane-tags">
                <el-tag size="mini" type="warning" effect="plain">待处理 {{ backlogMetric(supplier.value, 'recent').ready }}</el-tag>
                <el-tag size="mini" effect="plain">处理中 {{ backlogMetric(supplier.value, 'recent').processing }}</el-tag>
                <el-tag size="mini" type="info" effect="plain">延迟重试 {{ backlogMetric(supplier.value, 'recent').delayed_retry }}</el-tag>
              </div>
              <div class="lane-meta">
                <span>实际 Worker {{ backlogMetric(supplier.value, 'recent').actual_workers }}</span>
                <span>运行进程 {{ backlogMetric(supplier.value, 'recent').worker_processes }} / 配置 {{ backlogMetric(supplier.value, 'recent').configured_workers }}</span>
                <span>主数据阻塞 {{ backlogMetric(supplier.value, 'recent').blocked_master }}</span>
                <span>本分钟 入队/消费 {{ backlogMetric(supplier.value, 'recent').enqueued_minute }}/{{ backlogMetric(supplier.value, 'recent').consumed_minute }}</span>
                <span>本分钟 成功/失败 {{ backlogMetric(supplier.value, 'recent').success_minute }}/{{ backlogMetric(supplier.value, 'recent').failure_minute }}</span>
                <span>最近耗时 {{ backlogMetric(supplier.value, 'recent').last_task_duration_ms }} ms</span>
                <span>构建耗时 {{ backlogMetric(supplier.value, 'recent').last_build_duration_ms }} ms</span>
                <span>最近请求 {{ formatBytes(backlogMetric(supplier.value, 'recent').last_payload_bytes) }}（库存 {{ backlogMetric(supplier.value, 'recent').last_inventory_count }} / 价格 {{ backlogMetric(supplier.value, 'recent').last_rate_count }}）</span>
                <span>Go 堆内存 {{ formatBytes(backlogMetric(supplier.value, 'recent').go_heap_inuse_bytes) }}</span>
                <span>Go 内存上限 {{ formatBytes(backlogMetric(supplier.value, 'recent').go_memory_limit_bytes) }}</span>
                <span>每 Worker {{ backlogPerWorker(supplier.value, 'recent') }}</span>
                <span>最早等待 {{ formatWait(backlogMetric(supplier.value, 'recent').oldest_wait_seconds) }}</span>
                <span>最后成功 {{ backlogMetric(supplier.value, 'recent').last_success_at || '-' }}</span>
                <span v-if="backlogMetric(supplier.value, 'recent').last_failure_summary" class="lane-failure">最近失败 {{ backlogMetric(supplier.value, 'recent').last_failure_summary }}</span>
              </div>
            </div>
            <el-divider class="lane-divider" />
            <div class="lane-block lane-block--realtime" :class="{ disabled: !hasMidFutureWindow(supplier.value) }">
              <div class="lane-title"><span>中远期实时 <el-tag size="mini" :type="laneStatusTagType(supplier.value, 'future')">{{ laneStatusText(supplier.value, 'future') }}</el-tag></span><strong>{{ backlogMetric(supplier.value, 'future').ready }}</strong></div>
              <el-progress :percentage="backlogPercentage(supplier.value, 'future')" :status="backlogProgressStatus(supplier.value, 'future')" :show-text="false" />
              <div class="lane-tags">
                <el-tag size="mini" type="warning" effect="plain">待处理 {{ backlogMetric(supplier.value, 'future').ready }}</el-tag>
                <el-tag size="mini" effect="plain">处理中 {{ backlogMetric(supplier.value, 'future').processing }}</el-tag>
                <el-tag size="mini" type="info" effect="plain">延迟重试 {{ backlogMetric(supplier.value, 'future').delayed_retry }}</el-tag>
              </div>
              <div class="lane-meta">
                <span>实际 Worker {{ backlogMetric(supplier.value, 'future').actual_workers }}</span>
                <span>运行进程 {{ backlogMetric(supplier.value, 'future').worker_processes }} / 配置 {{ backlogMetric(supplier.value, 'future').configured_workers }}</span>
                <span>主数据阻塞 {{ backlogMetric(supplier.value, 'future').blocked_master }}</span>
                <span>本分钟 入队/消费 {{ backlogMetric(supplier.value, 'future').enqueued_minute }}/{{ backlogMetric(supplier.value, 'future').consumed_minute }}</span>
                <span>本分钟 成功/失败 {{ backlogMetric(supplier.value, 'future').success_minute }}/{{ backlogMetric(supplier.value, 'future').failure_minute }}</span>
                <span>最近耗时 {{ backlogMetric(supplier.value, 'future').last_task_duration_ms }} ms</span>
                <span>构建耗时 {{ backlogMetric(supplier.value, 'future').last_build_duration_ms }} ms</span>
                <span>最近请求 {{ formatBytes(backlogMetric(supplier.value, 'future').last_payload_bytes) }}（库存 {{ backlogMetric(supplier.value, 'future').last_inventory_count }} / 价格 {{ backlogMetric(supplier.value, 'future').last_rate_count }}）</span>
                <span>Go 堆内存 {{ formatBytes(backlogMetric(supplier.value, 'future').go_heap_inuse_bytes) }}</span>
                <span>Go 内存上限 {{ formatBytes(backlogMetric(supplier.value, 'future').go_memory_limit_bytes) }}</span>
                <span>每 Worker {{ backlogPerWorker(supplier.value, 'future') }}</span>
                <span>最早等待 {{ formatWait(backlogMetric(supplier.value, 'future').oldest_wait_seconds) }}</span>
                <span>最后成功 {{ backlogMetric(supplier.value, 'future').last_success_at || '-' }}</span>
                <span v-if="backlogMetric(supplier.value, 'future').last_failure_summary" class="lane-failure">最近失败 {{ backlogMetric(supplier.value, 'future').last_failure_summary }}</span>
              </div>
            </div>
            <el-divider class="lane-divider" />
            <div class="lane-block lane-block--recovery">
              <div class="lane-title"><span>预订失败紧急补推 <el-tag size="mini" :type="laneStatusTagType(supplier.value, 'booking_recovery')">{{ laneStatusText(supplier.value, 'booking_recovery') }}</el-tag></span><strong>{{ backlogMetric(supplier.value, 'booking_recovery').ready }}</strong></div>
              <el-progress :percentage="backlogPercentage(supplier.value, 'booking_recovery')" :status="backlogProgressStatus(supplier.value, 'booking_recovery')" :show-text="false" />
              <div class="lane-tags">
                <el-tag size="mini" type="danger" effect="plain">最高优先级</el-tag>
                <el-tag size="mini" type="warning" effect="plain">待处理 {{ backlogMetric(supplier.value, 'booking_recovery').ready }}</el-tag>
                <el-tag size="mini" effect="plain">处理中 {{ backlogMetric(supplier.value, 'booking_recovery').processing }}</el-tag>
                <el-tag size="mini" type="info" effect="plain">延迟重试 {{ backlogMetric(supplier.value, 'booking_recovery').delayed_retry }}</el-tag>
              </div>
              <div class="lane-meta">
                <span>配置 Worker {{ backlogMetric(supplier.value, 'booking_recovery').configured_workers }}</span>
                <span>最早等待 {{ formatWait(backlogMetric(supplier.value, 'booking_recovery').oldest_wait_seconds) }}</span>
                <span>最后成功 {{ backlogMetric(supplier.value, 'booking_recovery').last_success_at || '-' }}</span>
                <span v-if="backlogMetric(supplier.value, 'booking_recovery').last_failure_summary" class="lane-failure">最近失败 {{ backlogMetric(supplier.value, 'booking_recovery').last_failure_summary }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <el-form ref="form" :model="form" :rules="rules" class="worker-form">
        <div class="global-setting">
          <div>
            <div class="setting-title">总并发上限</div>
            <div class="setting-description">仅在供应商没有独立配置时作为自动分配上限。</div>
          </div>
          <el-form-item prop="total" class="global-input">
            <el-input-number v-model="form.total" :min="1" :max="512" :step="1" controls-position="right" />
          </el-form-item>
        </div>
        <el-table :data="suppliers" border stripe class="supplier-table">
          <el-table-column prop="label" label="供应商" width="100" fixed="left" />
          <el-table-column label="全量校准并发" min-width="150" align="center">
            <template slot-scope="scope">
              <el-input-number v-model="form.suppliers[scope.row.value]" :min="1" :max="256" :step="1" controls-position="right" size="small" />
            </template>
          </el-table-column>
          <el-table-column label="推送天数" min-width="135" align="center">
            <template slot-scope="scope">
              <el-input-number v-model="form.push_days[scope.row.value]" :min="1" :max="366" :step="1" controls-position="right" size="small" />
            </template>
          </el-table-column>
          <el-table-column label="近期范围" min-width="190">
            <template slot-scope="scope">
              <div class="range-text">{{ recentRangeText(scope.row.value) }}</div>
              <div v-if="!hasMidFutureWindow(scope.row.value)" class="range-muted">当前没有中远期日期</div>
            </template>
          </el-table-column>
          <el-table-column label="近期实时并发" min-width="150" align="center">
            <template slot-scope="scope">
              <el-input-number v-model="form.realtime_recent_workers[scope.row.value]" :min="1" :max="512" :step="1" controls-position="right" size="small" />
            </template>
          </el-table-column>
          <el-table-column label="中远期实时并发" min-width="170" align="center">
            <template slot-scope="scope">
              <el-input-number v-model="form.realtime_mid_future_workers[scope.row.value]" :min="1" :max="512" :step="1" controls-position="right" size="small" />
              <div v-if="!hasMidFutureWindow(scope.row.value)" class="range-muted">保存但暂不启用</div>
            </template>
          </el-table-column>
          <el-table-column label="预订失败补推并发" min-width="170" align="center">
            <template slot-scope="scope">
              <el-input-number v-model="form.booking_recovery_workers[scope.row.value]" :min="1" :max="64" :step="1" controls-position="right" size="small" />
            </template>
          </el-table-column>
        </el-table>
      </el-form>
      <el-alert
        class="window-warning"
        title="缩短推送天数后，超出新窗口但此前已推送成功的日期会在后续同步中显式关房；扩大后会逐步补齐新增日期。"
        type="warning"
        :closable="false"
        show-icon
      />
      <el-divider />
      <div class="summary">
        <span>全量校准并发合计：<b>{{ supplierTotal }}</b></span>
        <span>近期实时并发合计：<b>{{ recentWorkerTotal }}</b></span>
        <span>中远期实时并发合计：<b>{{ midFutureWorkerTotal }}</b></span>
        <span>预订失败补推并发合计：<b>{{ bookingRecoveryWorkerTotal }}</b></span>
        <span>最近保存：{{ updatedAt || '-' }}</span>
      </div>
      <el-alert
        v-if="supplierTotal !== Number(form.total)"
        class="sum-warning"
        title="全量校准并发合计与总并发上限不同；供应商独立配置优先，总并发不会强行截断已配置值。"
        type="warning"
        :closable="false"
      />
    </el-card>

    <el-dialog
      :visible.sync="failureDialogVisible"
      width="88%"
      top="4vh"
      custom-class="failure-detail-dialog"
      :close-on-click-modal="false"
    >
      <div slot="title" class="failure-dialog-title">
        <span class="failure-dialog-title__icon"><i class="el-icon-warning-outline" /></span>
        <div>
          <strong>{{ failureDialogTitle }}</strong>
          <small>定位全量校准失败酒店、阻断环节与是否可安全重试</small>
        </div>
      </div>
      <div class="failure-overview">
        <div class="failure-metric failure-metric--danger">
          <span>本范围失败</span>
          <strong>{{ failureDialogActiveTotal }}</strong>
          <small>{{ failureDialogScope === 'ignored' ? '已忽略并保留的证据' : '当前仍需处理的酒店' }}</small>
        </div>
        <div class="failure-metric" :class="{ 'failure-metric--filtered': failureDialogReasonCode }">
          <span>筛选结果</span>
          <strong>{{ failureDialogTotal }}</strong>
          <small>{{ failureDialogReasonCode ? '当前原因分类命中数' : '当前范围全部记录' }}</small>
        </div>
        <div class="failure-metric">
          <span>原因分类</span>
          <strong>{{ failureDialogSummary.length }}</strong>
          <small>按失败根因聚合</small>
        </div>
        <div class="failure-metric failure-metric--scope">
          <span>当前视图</span>
          <strong>{{ failureDialogScope === 'ignored' ? '已忽略' : '待处理' }}</strong>
          <small>可切换查看另一范围</small>
        </div>
      </div>
      <el-alert
        v-if="failureDialogMissingDetails > 0"
        class="failure-history-alert"
        :title="`${failureDialogMissingDetails} 家为功能发布前产生的历史失败；点击“重试失败项”后会补齐具体原因。`"
        type="warning"
        :closable="false"
        show-icon
      />
      <div class="failure-filter-panel">
        <div class="failure-filter-row">
          <span class="failure-filter-label">数据范围</span>
          <el-radio-group v-model="failureDialogScope" size="small" @change="changeFailureScope">
            <el-radio-button label="active">当前失败</el-radio-button>
            <el-radio-button label="ignored">已忽略证据</el-radio-button>
          </el-radio-group>
          <span class="failure-filter-help"><i class="el-icon-info" /> 切换范围不会修改任务状态</span>
        </div>
        <div class="failure-filter-row failure-filter-row--reasons">
          <span class="failure-filter-label">失败原因</span>
          <div class="failure-summary">
            <el-tag
              :type="failureDialogReasonCode === '' ? 'danger' : 'info'"
              :effect="failureDialogReasonCode === '' ? 'dark' : 'plain'"
              :class="{ 'is-selected': failureDialogReasonCode === '' }"
              @click="filterFailureReason('')"
            >全部 <b>{{ failureDialogActiveTotal }}</b></el-tag>
            <el-tag
              v-for="item in failureDialogSummary"
              :key="item.reason_code"
              :type="failureReasonTagType(item.reason_code)"
              :effect="failureDialogReasonCode === item.reason_code ? 'dark' : 'plain'"
              :class="{ 'is-selected': failureDialogReasonCode === item.reason_code }"
              @click="filterFailureReason(item.reason_code)"
            >{{ item.reason_text }} <b>{{ item.count }}</b></el-tag>
          </div>
        </div>
      </div>
      <div class="failure-table-shell">
        <div class="failure-table-heading">
          <div>
            <strong>失败酒店明细</strong>
            <span>优先处理不可重试及静态主数据未接受的问题</span>
          </div>
          <span>第 {{ failureDialogPage }} 页 · 共 {{ failureDialogTotal }} 条</span>
        </div>
        <el-table v-loading="failureDialogLoading" :data="failureDialogRows" border stripe max-height="500" empty-text="当前筛选条件下没有失败记录">
          <el-table-column label="酒店编码" min-width="150" fixed="left">
            <template slot-scope="scope">
              <span class="failure-hotel-code">{{ scope.row.hotel_code || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="失败分类" min-width="220">
            <template slot-scope="scope">
              <el-tag size="mini" :type="failureReasonTagType(scope.row.reason_code)" effect="dark">{{ scope.row.reason_text || '未知失败' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="具体原因" min-width="400">
            <template slot-scope="scope">
              <div class="failure-message" :class="{ 'failure-message--unknown': scope.row.reason_code === 'unknown' }">
                <i :class="scope.row.reason_code === 'unknown' ? 'el-icon-question' : 'el-icon-warning-outline'" />
                <span>{{ scope.row.message || '未记录具体错误信息，请重试后重新采集' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="阻断资源" min-width="210">
            <template slot-scope="scope">
              <div class="failure-resource">
                <span>{{ scope.row.resource_type || '未定位资源类型' }}</span>
                <code>{{ scope.row.resource_code || '-' }}</code>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="失败时间" min-width="190">
            <template slot-scope="scope">
              <span class="failure-time"><i class="el-icon-time" /> {{ scope.row.failed_at || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="处理建议" width="110" align="center" fixed="right">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.retryable ? 'success' : 'danger'" effect="plain">{{ scope.row.retryable ? '可以重试' : '需先修复' }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
        <div class="failure-pagination">
          <el-pagination
            background
            layout="total, prev, pager, next"
            :current-page="failureDialogPage"
            :page-size="failureDialogLimit"
            :total="failureDialogTotal"
            @current-change="changeFailurePage"
          />
        </div>
      </div>
      <div slot="footer" class="failure-dialog-footer">
        <span><i class="el-icon-info" /> 失败明细仅用于诊断，不会自动触发重试或忽略。</span>
        <el-button @click="failureDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import DidaModuleHeader from './DidaModuleHeader.vue'

export default {
  name: 'DidaWorkerControl',
  components: { DidaModuleHeader },
  data() {
    return {
      saving: false,
      configLoading: false,
      configError: '',
      controlMode: 'worker_only',
      qpsEnabled: false,
      backlogLoading: false,
      backlogStatus: 'loading',
      backlogMessage: '',
      backlogSampledAt: '',
      backlogTimer: null,
      backlogBySupplier: {},
      fullActionLoading: '',
      failureDialogVisible: false,
      failureDialogLoading: false,
      failureDialogSupplier: '',
      failureDialogScope: 'active',
      failureDialogReasonCode: '',
      failureDialogRows: [],
      failureDialogSummary: [],
      failureDialogTotal: 0,
      failureDialogActiveTotal: 0,
      failureDialogMissingDetails: 0,
      failureDialogPage: 1,
      failureDialogLimit: 20,
      updatedAt: '',
      suppliers: [
        { label: '锦江', value: 'jin_jiang', recentDays: 20 },
        { label: '隧穿亚朵', value: 'sui_chuan', recentDays: 15 },
        { label: '丽呈', value: 'li_cheng', recentDays: 22 },
        { label: '龙腾', value: 'long_teng', recentDays: 9 },
        { label: '雅斯特', value: 'ya_si_te', recentDays: 20 },
      ],
      form: {
        total: 254,
        suppliers: { jin_jiang: 100, sui_chuan: 4, li_cheng: 50, long_teng: 100, ya_si_te: 1 },
        push_days: { jin_jiang: 90, sui_chuan: 20, li_cheng: 150, long_teng: 20, ya_si_te: 90 },
        realtime_recent_workers: { jin_jiang: 100, sui_chuan: 4, li_cheng: 50, long_teng: 100, ya_si_te: 1 },
        realtime_mid_future_workers: { jin_jiang: 20, sui_chuan: 1, li_cheng: 10, long_teng: 10, ya_si_te: 1 },
        booking_recovery_workers: { jin_jiang: 4, sui_chuan: 4, li_cheng: 4, long_teng: 4, ya_si_te: 4 },
      },
      rules: {
        total: [{ required: true, message: '请输入总并发数', trigger: 'change' }],
      },
    }
  },
  computed: {
    moduleStatus() {
      if (this.policyConflict) return 'danger'
      if (this.backlogStatus === 'unavailable' || this.backlogStatus === 'backlogged' || this.configError) return 'warning'
      return this.suppliers.some(item => this.supplierBacklogType(item.value) === 'danger') ? 'warning' : 'healthy'
    },
    moduleStatusText() {
      if (this.policyConflict) return '执行策略冲突'
      if (this.configError) return '配置读取失败'
      if (this.backlogStatus === 'unavailable') return '积压监控不可用'
      if (this.backlogStatus === 'backlogged') return '实时推送严重积压'
      return this.moduleStatus === 'warning' ? '存在推送积压' : 'Worker 通道正常'
    },
    policyConflict() {
      return this.controlMode !== 'worker_only' || this.qpsEnabled
    },
    controlModeText() {
      return this.controlMode === 'worker_only' ? 'Worker Only' : this.controlMode
    },
    channelCount() {
      return this.suppliers.length * 4
    },
    configuredWorkerTotal() {
      return this.supplierTotal + this.recentWorkerTotal + this.midFutureWorkerTotal + this.bookingRecoveryWorkerTotal
    },
    readyBacklogTotal() {
      return this.suppliers.reduce((sum, item) => sum + Number(this.backlogMetric(item.value, 'recent').ready || 0) + Number(this.backlogMetric(item.value, 'future').ready || 0) + Number(this.backlogMetric(item.value, 'booking_recovery').ready || 0), 0)
    },
    delayedRetryTotal() {
      return this.suppliers.reduce((sum, item) => sum + Number(this.backlogMetric(item.value, 'recent').delayed_retry || 0) + Number(this.backlogMetric(item.value, 'future').delayed_retry || 0) + Number(this.backlogMetric(item.value, 'booking_recovery').delayed_retry || 0), 0)
    },
    processingTotal() {
      return this.suppliers.reduce((sum, item) => sum + Number(this.backlogMetric(item.value, 'recent').processing || 0) + Number(this.backlogMetric(item.value, 'future').processing || 0) + Number(this.backlogMetric(item.value, 'booking_recovery').processing || 0), 0)
    },
    supplierTotal() {
      return this.suppliers.reduce((sum, item) => sum + Number(this.form.suppliers[item.value] || 0), 0)
    },
    recentWorkerTotal() {
      return this.suppliers.reduce((sum, item) => sum + Number(this.form.realtime_recent_workers[item.value] || 0), 0)
    },
    midFutureWorkerTotal() {
      return this.suppliers.reduce((sum, item) => sum + Number(this.form.realtime_mid_future_workers[item.value] || 0), 0)
    },
    bookingRecoveryWorkerTotal() {
      return this.suppliers.reduce((sum, item) => sum + Number(this.form.booking_recovery_workers[item.value] || 0), 0)
    },
    failureDialogTitle() {
      const supplier = this.suppliers.find(item => item.value === this.failureDialogSupplier)
      const supplierName = supplier ? supplier.label : this.failureDialogSupplier
      const scopeName = this.failureDialogScope === 'ignored' ? '已忽略失败' : '当前失败'
      return `${supplierName || 'Dida'}全量校准 · ${scopeName}`
    },
  },
  created() {
    this.load()
    this.loadBacklog()
    this.backlogTimer = setInterval(() => {
      if (!document.hidden) this.loadBacklog()
    }, 10000)
  },
  beforeDestroy() {
    if (this.backlogTimer) clearInterval(this.backlogTimer)
  },
  methods: {
    refreshDashboard() {
      return Promise.all([this.load(), this.loadBacklog()])
    },
    emptyBacklog() {
      return { total: 0, ready: 0, processing: 0, delayed_retry: 0, oldest_wait_seconds: 0, actual_workers: 0, configured_workers: 0, worker_processes: 0, blocked_master: 0, enqueued_minute: 0, consumed_minute: 0, success_minute: 0, failure_minute: 0, last_task_duration_ms: 0, last_build_duration_ms: 0, last_payload_bytes: 0, last_inventory_count: 0, last_rate_count: 0, go_heap_inuse_bytes: 0, go_memory_limit_bytes: 0, worker_heartbeat_stale: true, last_consume_at: null, last_success_at: null, last_failure_at: null, last_failure_summary: '' }
    },
    emptyFull() {
      return { status: 'idle', authorized_hotels: 0, processed_hotels: 0, remaining_hotels: 0, progress_percent: 0, succeeded: 0, failed: 0, failure_events: 0, retry_hotels: 0, retry_total: 0, retry_processed: 0, retry_remaining: 0, retry_progress_percent: 0, retry_started_at: null, retry_finished_at: null, ignored_failures: 0, heartbeat_age_seconds: null, last_batch_at: null }
    },
    fullMetric(supplier) {
      const row = this.backlogBySupplier[supplier]
      return row && row.full ? row.full : this.emptyFull()
    },
    backlogMetric(supplier, segment) {
      const row = this.backlogBySupplier[supplier]
      return row && row[segment] ? row[segment] : this.emptyBacklog()
    },
    backlogWorkers(supplier, segment) {
      const metric = this.backlogMetric(supplier, segment)
      return Math.max(0, Number(metric.configured_workers || metric.worker_processes || 0))
    },
    backlogPerWorker(supplier, segment) {
      const ready = Number(this.backlogMetric(supplier, segment).ready || 0)
      const workers = this.backlogWorkers(supplier, segment)
      return workers > 0 ? (ready / workers).toFixed(1) : ready.toFixed(1)
    },
    backlogLevel(supplier, segment) {
      const metric = this.backlogMetric(supplier, segment)
      if (Number(metric.ready || 0) > 0 && (metric.worker_heartbeat_stale || Number(metric.worker_processes || 0) <= 0)) return 'danger'
      if (Number(metric.oldest_wait_seconds || 0) > 600) return 'danger'
      if (Number(metric.blocked_master || 0) > 0 || Number(metric.failure_minute || 0) > 0) return 'warning'
      const value = Number(this.backlogPerWorker(supplier, segment))
      if (value > 5) return 'danger'
      if (value > 1) return 'warning'
      return 'success'
    },
    backlogPercentage(supplier, segment) {
      return Math.min(100, Math.round(Number(this.backlogPerWorker(supplier, segment)) * 10))
    },
    backlogProgressStatus(supplier, segment) {
      return this.backlogLevel(supplier, segment) === 'danger' ? 'exception' : this.backlogLevel(supplier, segment)
    },
    supplierBacklogType(supplier) {
      const full = this.fullLevel(supplier)
      const recent = this.backlogLevel(supplier, 'recent')
      const future = this.hasMidFutureWindow(supplier) ? this.backlogLevel(supplier, 'future') : 'success'
      const recovery = this.backlogLevel(supplier, 'booking_recovery')
      if (full === 'danger' || recent === 'danger' || future === 'danger' || recovery === 'danger') return 'danger'
      if (full === 'warning' || recent === 'warning' || future === 'warning' || recovery === 'warning') return 'warning'
      return 'success'
    },
    supplierBacklogText(supplier) {
      const type = this.supplierBacklogType(supplier)
      return type === 'danger' ? '异常' : (type === 'warning' ? '关注' : '正常')
    },
    fullLevel(supplier) {
      const full = this.fullMetric(supplier)
      if (this.isFullRetryStalled(supplier)) return 'danger'
      if (['retry_requested', 'ignore_requested', 'retrying_failures'].includes(full.status)) return 'warning'
      if (full.status === 'completed_with_errors' || Number(full.retry_hotels || 0) > 0) return 'danger'
      if (full.status === 'running' && Number(full.heartbeat_age_seconds || 0) > 300) return 'danger'
      if (['idle', 'replay_requested'].includes(full.status) || Number(full.failed || 0) > 0) return 'warning'
      return 'success'
    },
    fullProgressStatus(supplier) {
      if (this.isFullRetrying(supplier)) return undefined
      const level = this.fullLevel(supplier)
      return level === 'danger' ? 'exception' : level
    },
    fullProgressColor(supplier) {
      if (this.isFullRetryStalled(supplier)) return '#f56c6c'
      return this.isFullRetrying(supplier) ? '#e6a23c' : undefined
    },
    isFullRetrying(supplier) {
      return ['retry_requested', 'retrying_failures'].includes(this.fullMetric(supplier).status)
    },
    isFullRetryStalled(supplier) {
      const full = this.fullMetric(supplier)
      return full.status === 'retrying_failures' && Number(full.heartbeat_age_seconds || 0) > 300
    },
    fullDisplayProgress(supplier) {
      const full = this.fullMetric(supplier)
      const value = this.isFullRetrying(supplier) ? full.retry_progress_percent : full.progress_percent
      return Math.max(0, Math.min(100, Number(value || 0)))
    },
    fullRetryStatusText(supplier) {
      const full = this.fullMetric(supplier)
      if (full.status === 'retry_requested') return `等待 Go Worker 接管，共 ${Number(full.retry_total || 0)} 家`
      if (this.isFullRetryStalled(supplier)) return `超过 5 分钟没有新批次，剩余 ${Number(full.retry_remaining || 0)} 家，请检查 Worker`
      return `已完成 ${Number(full.retry_processed || 0)} 家，剩余 ${Number(full.retry_remaining || 0)} 家`
    },
    fullStatusTagType(supplier) {
      const level = this.fullLevel(supplier)
      return level === 'danger' ? 'danger' : (level === 'warning' ? 'warning' : 'success')
    },
    laneStatusTagType(supplier, segment) {
      if (segment === 'future' && !this.hasMidFutureWindow(supplier)) return 'info'
      const level = this.backlogLevel(supplier, segment)
      return level === 'danger' ? 'danger' : (level === 'warning' ? 'warning' : 'success')
    },
    laneStatusText(supplier, segment) {
      if (segment === 'future' && !this.hasMidFutureWindow(supplier)) return '未启用'
      const level = this.backlogLevel(supplier, segment)
      return level === 'danger' ? '异常' : (level === 'warning' ? '关注' : '顺畅')
    },
    fullStatusText(status) {
      return { idle: '尚无校准记录', running: '校准进行中', completed: '本轮已完成', completed_with_errors: '完成但有失败', completed_ignored: '本轮异常已忽略', replay_requested: '已请求重跑', retry_requested: '等待重试失败项', retrying_failures: '正在重试失败项', ignore_requested: '等待忽略确认' }[status] || status || '未知'
    },
    canHandleFullFailure(supplier) {
      const full = this.fullMetric(supplier)
      if (['retry_requested', 'ignore_requested', 'retrying_failures'].includes(full.status)) return false
      return Number(full.failed || 0) > 0 || Number(full.retry_hotels || 0) > 0
    },
    hasFullFailureEvidence(supplier) {
      const full = this.fullMetric(supplier)
      return Number(full.failed || 0) > 0 || Number(full.retry_hotels || 0) > 0 || Number(full.ignored_failures || 0) > 0
    },
    openFullFailureDetails(supplier) {
      const full = this.fullMetric(supplier)
      this.failureDialogSupplier = supplier
      this.failureDialogScope = Number(full.failed || 0) > 0 || Number(full.retry_hotels || 0) > 0 ? 'active' : 'ignored'
      this.failureDialogReasonCode = ''
      this.failureDialogPage = 1
      this.failureDialogVisible = true
      return this.loadFullFailureDetails()
    },
    loadFullFailureDetails() {
      if (!this.failureDialogSupplier) return Promise.resolve()
      this.failureDialogLoading = true
      return request({
        url: '/api/backend/dida/worker-controls/full-calibration/failures',
        method: 'get',
        params: {
          supplier: this.failureDialogSupplier,
          scope: this.failureDialogScope,
          reason_code: this.failureDialogReasonCode,
          page: this.failureDialogPage,
          limit: this.failureDialogLimit,
        },
      }).then(response => {
        const data = response.data || {}
        this.failureDialogRows = data.items || []
        this.failureDialogSummary = data.reason_summary || []
        this.failureDialogTotal = Number(data.total || 0)
        this.failureDialogActiveTotal = Number(data.active_total || 0)
        const unknown = this.failureDialogSummary.find(item => item.reason_code === 'unknown')
        this.failureDialogMissingDetails = Number(unknown ? unknown.count : 0)
      }).catch(() => {
        this.failureDialogRows = []
        this.failureDialogSummary = []
        this.failureDialogTotal = 0
        this.failureDialogActiveTotal = 0
        this.failureDialogMissingDetails = 0
        this.$message.error('全量校准失败明细读取失败，请稍后重试')
      }).finally(() => {
        this.failureDialogLoading = false
      })
    },
    filterFailureReason(reasonCode) {
      this.failureDialogReasonCode = reasonCode
      this.failureDialogPage = 1
      return this.loadFullFailureDetails()
    },
    changeFailureScope() {
      this.failureDialogReasonCode = ''
      this.failureDialogPage = 1
      return this.loadFullFailureDetails()
    },
    failureReasonTagType(reasonCode) {
      if (['timeout', 'context_canceled', 'provider_retryable'].includes(reasonCode)) return 'warning'
      if (reasonCode === 'unknown') return 'info'
      if (reasonCode === 'no_eligible_source_resource') return 'warning'
      return 'danger'
    },
    changeFailurePage(page) {
      this.failureDialogPage = page
      return this.loadFullFailureDetails()
    },
    submitFullCalibrationAction(supplier, action, reason = '') {
      this.fullActionLoading = `${supplier}:${action}`
      return request({
        url: '/api/backend/dida/worker-controls/full-calibration/action',
        method: 'post',
        data: { supplier, action, reason },
      }).then(response => {
        if (action === 'retry') this.markRetryRequested(supplier)
        this.$message.success(response.msg || (action === 'retry' ? '已提交失败项重试' : '已忽略本轮异常'))
        return this.loadBacklog()
      }).finally(() => {
        this.fullActionLoading = ''
      })
    },
    markRetryRequested(supplier) {
      const row = this.backlogBySupplier[supplier]
      if (!row || !row.full) return
      const full = row.full
      const retryTotal = Math.max(Number(full.failed || 0), Number(full.retry_hotels || 0), Number(full.retry_total || 0))
      this.$set(row, 'full', {
        ...full,
        status: 'retry_requested',
        retry_total: retryTotal,
        retry_processed: 0,
        retry_remaining: retryTotal,
        retry_progress_percent: 0,
        operator_action: 'retry',
        operator_action_requested_at: new Date().toLocaleString(),
      })
    },
    retryFullCalibration(supplier) {
      return this.$confirm('只重试该供应商本轮失败酒店；若旧数据没有保留失败酒店明细，将自动执行一次完整差异校准。是否继续？', '重试全量校准失败项', {
        type: 'warning',
        confirmButtonText: '确认重试',
        cancelButtonText: '取消',
      }).then(() => this.submitFullCalibrationAction(supplier, 'retry'))
    },
    ignoreFullCalibration(supplier) {
      return this.$prompt('忽略后本轮告警恢复正常，但失败数量、酒店明细、操作时间和原因仍会保留 30 天。', '忽略本轮异常', {
        type: 'warning',
        inputPlaceholder: '请输入忽略原因（可选）',
        inputValidator: value => !value || value.length <= 200 || '原因不能超过 200 个字符',
        confirmButtonText: '确认忽略',
        cancelButtonText: '取消',
      }).then(({ value }) => this.submitFullCalibrationAction(supplier, 'ignore', value || ''))
    },
    formatWait(seconds) {
      const value = Number(seconds || 0)
      if (value < 60) return `${value} 秒`
      if (value < 3600) return `${Math.floor(value / 60)} 分钟`
      return `${Math.floor(value / 3600)} 小时 ${Math.floor((value % 3600) / 60)} 分钟`
    },
    formatBytes(bytes) {
      const value = Number(bytes || 0)
      if (value <= 0) return '-'
      if (value < 1024) return `${value} B`
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
      if (value < 1024 * 1024 * 1024) return `${(value / 1024 / 1024).toFixed(1)} MB`
      return `${(value / 1024 / 1024 / 1024).toFixed(2)} GB`
    },
    loadBacklog() {
      if (this.backlogLoading) return Promise.resolve()
      this.backlogLoading = true
      return request({ url: '/api/backend/dida/worker-controls/backlog', method: 'get' }).then(response => {
        const data = response.data || {}
        const values = {}
        ;(data.suppliers || []).forEach(item => { values[item.scope] = item })
        this.backlogBySupplier = values
        this.backlogStatus = data.status || 'ok'
        this.backlogMessage = data.message || ''
        this.backlogSampledAt = data.sampled_at || ''
      }).catch(() => {
        this.backlogStatus = 'unavailable'
        this.backlogMessage = '推送积压接口请求失败，请检查后台与 Redis 连接'
      }).finally(() => {
        this.backlogLoading = false
      })
    },
    recentRangeText(supplier) {
      const item = this.suppliers.find(value => value.value === supplier)
      const pushDays = Number(this.form.push_days[supplier] || 0)
      const recentDays = Number(item ? item.recentDays : 0)
      if (pushDays > 0 && recentDays > 0 && pushDays <= recentDays) {
        return `全部 ${pushDays} 天（从昨日开始）`
      }
      const ranges = {
        jin_jiang: '昨日到未来第18天',
        sui_chuan: '昨日到未来第13天',
        li_cheng: '昨日到未来第20天',
        long_teng: '昨日到未来第7天',
        ya_si_te: '昨日到未来第18天',
      }
      return ranges[supplier] || '对应房态 worker 窗口'
    },
    hasMidFutureWindow(supplier) {
      const item = this.suppliers.find(value => value.value === supplier)
      return Number(this.form.push_days[supplier] || 0) > Number(item ? item.recentDays : 0)
    },
    load() {
      if (this.configLoading) return Promise.resolve()
      this.configLoading = true
      this.configError = ''
      return request({ url: '/api/backend/dida/worker-controls', method: 'get' }).then(response => {
        const data = response.data || {}
        const policy = data.execution_policy || {}
        this.controlMode = policy.mode || 'worker_only'
        this.qpsEnabled = policy.qps_limit_enabled === true
        this.form.total = Number(data.total || this.form.total)
        ;(data.suppliers || []).forEach(item => {
          if (Object.prototype.hasOwnProperty.call(this.form.suppliers, item.scope)) {
            this.$set(this.form.suppliers, item.scope, Number(item.worker_count))
            this.$set(this.form.push_days, item.scope, Number(item.push_days || 20))
            this.$set(this.form.realtime_recent_workers, item.scope, Number(item.realtime_recent_workers || item.worker_count || 1))
            this.$set(this.form.realtime_mid_future_workers, item.scope, Number(item.realtime_mid_future_workers || 1))
            this.$set(this.form.booking_recovery_workers, item.scope, Number(item.booking_recovery_workers || 4))
            const supplier = this.suppliers.find(value => value.value === item.scope)
            if (supplier && Number(item.recent_days) > 0) supplier.recentDays = Number(item.recent_days)
          }
        })
        this.updatedAt = data.updated_at || ''
      }).catch(() => {
        this.configError = 'Worker 配置读取失败，当前页面数值可能不是线上实际配置，请勿直接保存。'
      }).finally(() => {
        this.configLoading = false
      })
    },
    save() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.saving = true
        const payload = {
          total: Number(this.form.total),
          suppliers: { ...this.form.suppliers },
          push_days: { ...this.form.push_days },
          realtime_recent_workers: { ...this.form.realtime_recent_workers },
          realtime_mid_future_workers: { ...this.form.realtime_mid_future_workers },
          booking_recovery_workers: { ...this.form.booking_recovery_workers },
        }
        request({
          url: '/api/backend/dida/worker-controls/save',
          method: 'post',
          data: payload,
        }).then(response => {
          const data = response.data || {}
          this.updatedAt = data.updated_at || new Date().toLocaleString()
          this.$message.success(response.msg || 'Worker并发及推送天数配置已保存，下一轮调度生效')
          return Promise.all([this.load(), this.loadBacklog()])
        }).finally(() => {
          this.saving = false
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.help-card { width: 100%; }
.operations-overview { display: grid; grid-template-columns: repeat(4, minmax(180px, 1fr)); gap: 12px; margin-bottom: 14px; }
.overview-card { display: flex; min-height: 108px; padding: 16px 18px; border: 1px solid #e4e7ed; border-radius: 7px; background: #fff; box-shadow: 0 2px 10px rgba(31, 45, 61, .04); flex-direction: column; justify-content: center; }
.overview-card--policy { border-color: #b8dfc7; background: linear-gradient(135deg, #f1fbf5, #fff); }
.overview-card--warning { border-color: #f5d5a6; background: #fffbf3; }
.overview-label { color: #909399; font-size: 12px; }
.overview-card strong { margin: 7px 0 4px; color: #303133; font-size: 26px; line-height: 30px; }
.overview-card--policy strong { color: #2f8f57; font-size: 22px; }
.overview-card small { color: #909399; }
.policy-alert { margin-bottom: 14px; }
.worker-form { margin-top: 20px; }
.backlog-board { margin-top: 18px; padding: 18px; border: 1px solid #ebeef5; border-radius: 4px; background: #fafafa; }
.backlog-heading, .backlog-card-title, .lane-title { display: flex; align-items: center; justify-content: space-between; }
.backlog-actions { display: flex; align-items: center; gap: 12px; }
.sample-time { color: #909399; font-size: 12px; }
.backlog-grid { display: grid; grid-template-columns: repeat(2, minmax(460px, 1fr)); gap: 16px; margin-top: 14px; }
.backlog-card { display: flex; min-width: 0; padding: 16px; border: 1px solid #dfe4ed; border-radius: 6px; background: #fff; box-shadow: 0 2px 8px rgba(31, 45, 61, .04); flex-direction: column; }
.backlog-card-title { margin-bottom: 12px; color: #303133; font-weight: 600; }
.lane-block { min-width: 0; }
.lane-block--full { display: flex; min-height: 198px; flex-direction: column; }
.lane-block--full.lane-block--retrying { min-height: 246px; margin: -8px; padding: 12px; border: 1px solid #f5dab1; border-radius: 8px; background: linear-gradient(135deg, #fffaf2, #fff); box-shadow: 0 4px 12px rgba(230, 162, 60, .08); }
.lane-block--realtime { min-height: 310px; }
.lane-block--recovery { min-height: 190px; }
.lane-block.disabled { opacity: .55; }
.lane-title { margin-bottom: 7px; color: #606266; }
.lane-title > span { display: flex; align-items: center; gap: 8px; font-weight: 600; }
.lane-title strong { color: #303133; font-size: 22px; }
.lane-block--retrying .lane-title strong { color: #e6a23c; }
.retry-progress-bar { margin-top: 2px; }
.retry-progress-bar ::v-deep .el-progress-bar__outer { background: #f5ead8; }
.retry-progress-bar ::v-deep .el-progress-bar__inner { background: linear-gradient(90deg, #f3bf68 0%, #e6a23c 40%, #ffd591 70%, #e6a23c 100%) !important; background-size: 220% 100% !important; animation: retry-progress-flow 1.4s linear infinite; }
.retry-visual-status { display: flex; align-items: center; gap: 10px; margin-top: 12px; padding: 10px 12px; border: 1px solid #f5dab1; border-radius: 6px; background: rgba(255, 255, 255, .82); }
.retry-visual-status strong, .retry-visual-status small { display: block; }
.retry-visual-status strong { color: #b87916; font-size: 13px; line-height: 20px; }
.retry-visual-status small { color: #909399; font-size: 12px; line-height: 18px; }
.retry-visual-status--stalled { border-color: #fbc4c4; background: #fff3f3; }
.retry-visual-status--stalled strong { color: #e64545; }
.retry-visual-status--stalled .retry-live-dot { background: #f56c6c; box-shadow: 0 0 0 4px rgba(245, 108, 108, .14); }
.retry-visual-status--stalled .retry-live-dot::after { border-color: rgba(245, 108, 108, .5); }
.retry-live-dot { position: relative; width: 9px; height: 9px; border-radius: 50%; background: #e6a23c; box-shadow: 0 0 0 4px rgba(230, 162, 60, .15); flex: 0 0 auto; }
.retry-live-dot::after { position: absolute; top: -5px; left: -5px; width: 19px; height: 19px; border: 1px solid rgba(230, 162, 60, .55); border-radius: 50%; content: ''; animation: retry-live-pulse 1.6s ease-out infinite; }
.lane-tags--retry { margin-top: 9px; }
.lane-meta--retry { margin-top: 8px; }
.retry-progress-copy { grid-column: 1 / -1; color: #b87916; font-weight: 600; }
@keyframes retry-progress-flow {
  from { background-position: 100% 0; }
  to { background-position: -120% 0; }
}
@keyframes retry-live-pulse {
  0% { opacity: .9; transform: scale(.65); }
  75%, 100% { opacity: 0; transform: scale(1.25); }
}
.lane-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.lane-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 10px; margin-top: 8px; color: #909399; font-size: 12px; line-height: 18px; }
.lane-failure { grid-column: 1 / -1; color: #f56c6c; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.full-actions { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 8px; min-height: 29px; margin-top: auto; padding-top: 12px; border-top: 1px dashed #ebeef5; }
.full-actions ::v-deep .el-button + .el-button { margin-left: 0; }
.failure-dialog-title { display: flex; align-items: center; gap: 12px; }
.failure-dialog-title__icon { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 10px; color: #fff; background: linear-gradient(135deg, #f56c6c, #d93434); box-shadow: 0 6px 14px rgba(245, 108, 108, .25); font-size: 20px; }
.failure-dialog-title strong, .failure-dialog-title small { display: block; }
.failure-dialog-title strong { color: #303133; font-size: 17px; line-height: 24px; }
.failure-dialog-title small { margin-top: 2px; color: #909399; font-size: 12px; font-weight: 400; }
.failure-overview { display: grid; grid-template-columns: repeat(4, minmax(150px, 1fr)); gap: 12px; margin-bottom: 16px; }
.failure-metric { display: flex; min-height: 92px; padding: 14px 16px; border: 1px solid #e4e7ed; border-radius: 8px; background: #fff; flex-direction: column; justify-content: center; }
.failure-metric > span { color: #909399; font-size: 12px; }
.failure-metric strong { margin: 4px 0 2px; color: #303133; font-size: 24px; line-height: 30px; }
.failure-metric small { color: #909399; font-size: 12px; }
.failure-metric--danger { border-color: #fbc4c4; background: linear-gradient(135deg, #fff3f3, #fff); }
.failure-metric--danger strong { color: #e64545; }
.failure-metric--filtered { border-color: #f5dab1; background: #fffaf2; }
.failure-metric--filtered strong { color: #e6a23c; }
.failure-metric--scope { border-color: #c6e2ff; background: #f5faff; }
.failure-metric--scope strong { color: #409eff; font-size: 20px; }
.failure-history-alert { margin-bottom: 16px; }
.failure-filter-panel { margin-bottom: 16px; padding: 14px 16px; border: 1px solid #e4e7ed; border-radius: 8px; background: #f8fafc; }
.failure-filter-row { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
.failure-filter-row--reasons { align-items: flex-start; margin-top: 14px; padding-top: 14px; border-top: 1px solid #ebeef5; }
.failure-filter-label { min-width: 64px; padding-top: 1px; color: #606266; font-size: 13px; font-weight: 600; }
.failure-filter-help { color: #909399; font-size: 12px; }
.failure-summary { display: flex; flex: 1; flex-wrap: wrap; gap: 8px; min-width: 0; }
.failure-summary .el-tag { height: 28px; padding: 0 10px; cursor: pointer; line-height: 26px; transition: transform .15s ease, box-shadow .15s ease; }
.failure-summary .el-tag:hover { transform: translateY(-1px); box-shadow: 0 3px 8px rgba(31, 45, 61, .12); }
.failure-summary .el-tag.is-selected { box-shadow: 0 3px 9px rgba(245, 108, 108, .22); font-weight: 600; }
.failure-summary .el-tag b { margin-left: 4px; }
.failure-table-shell { overflow: hidden; border: 1px solid #dfe4ed; border-radius: 8px; background: #fff; }
.failure-table-heading { display: flex; align-items: center; justify-content: space-between; min-height: 58px; padding: 0 16px; border-bottom: 1px solid #ebeef5; background: #fff; }
.failure-table-heading > div { display: flex; align-items: baseline; gap: 12px; }
.failure-table-heading strong { color: #303133; font-size: 14px; }
.failure-table-heading span { color: #909399; font-size: 12px; }
.failure-table-shell ::v-deep .el-table th { color: #606266; background: #f5f7fa; font-weight: 600; }
.failure-table-shell ::v-deep .el-table td { padding: 11px 0; }
.failure-hotel-code { color: #303133; font-family: Consolas, Monaco, monospace; font-weight: 600; }
.failure-message { display: flex; align-items: flex-start; gap: 7px; color: #d93434; line-height: 20px; }
.failure-message i { margin-top: 2px; flex: 0 0 auto; }
.failure-message--unknown { color: #909399; }
.failure-resource { display: flex; min-width: 0; flex-direction: column; gap: 3px; }
.failure-resource span { color: #606266; font-size: 12px; }
.failure-resource code { overflow: hidden; color: #303133; font-family: Consolas, Monaco, monospace; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.failure-time { color: #606266; font-size: 12px; white-space: nowrap; }
.failure-time i { margin-right: 3px; color: #909399; }
.failure-pagination { display: flex; justify-content: flex-end; min-height: 60px; padding: 14px 16px; border-top: 1px solid #ebeef5; background: #fafafa; box-sizing: border-box; }
.failure-dialog-footer { display: flex; align-items: center; justify-content: space-between; }
.failure-dialog-footer > span { color: #909399; font-size: 12px; }
.failure-dialog-footer > span i { margin-right: 4px; color: #409eff; }
.dida-worker-control ::v-deep .failure-detail-dialog { min-width: 920px; max-width: 1480px; border-radius: 10px; overflow: hidden; box-shadow: 0 18px 48px rgba(31, 45, 61, .2); }
.dida-worker-control ::v-deep .failure-detail-dialog .el-dialog__header { padding: 18px 24px; border-bottom: 1px solid #ebeef5; background: #fff; }
.dida-worker-control ::v-deep .failure-detail-dialog .el-dialog__headerbtn { top: 25px; right: 24px; }
.dida-worker-control ::v-deep .failure-detail-dialog .el-dialog__body { max-height: 78vh; padding: 20px 24px; overflow-y: auto; background: #f5f7fa; }
.dida-worker-control ::v-deep .failure-detail-dialog .el-dialog__footer { padding: 14px 24px; border-top: 1px solid #ebeef5; background: #fff; }
.lane-divider { margin: 14px 0; background-color: #e4e7ed; }
.global-setting { display: flex; align-items: center; justify-content: space-between; max-width: 520px; margin-bottom: 18px; padding: 14px 18px; background: #f5f7fa; border-radius: 4px; }
.global-input { margin-bottom: 0; }
.setting-title { color: #303133; font-weight: 600; }
.setting-description, .range-muted { margin-top: 4px; color: #909399; font-size: 12px; }
.supplier-table { width: 100%; }
.supplier-table ::v-deep .el-input-number { width: 120px; }
.range-text { color: #303133; line-height: 20px; }
.summary { display: flex; flex-wrap: wrap; gap: 16px 32px; color: #606266; }
.sum-warning { margin-top: 18px; }
.window-warning { margin: 14px 0 18px; }
@media (max-width: 900px) {
  .operations-overview { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .global-setting { max-width: none; }
  .summary { gap: 10px 18px; }
  .backlog-heading { align-items: flex-start; flex-direction: column; gap: 10px; }
  .backlog-grid { grid-template-columns: 1fr; }
  .lane-block--full, .lane-block--realtime, .lane-block--recovery { min-height: 0; }
  .failure-overview { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .dida-worker-control ::v-deep .failure-detail-dialog { width: calc(100% - 32px) !important; min-width: 0; }
}
@media (min-width: 901px) and (max-width: 1100px) { .backlog-grid { grid-template-columns: 1fr; } }
@media (max-width: 520px) {
  .operations-overview, .failure-overview { grid-template-columns: 1fr; }
  .full-actions { align-items: stretch; flex-direction: column; }
  .full-actions ::v-deep .el-button { width: 100%; }
  .failure-dialog-footer { align-items: stretch; flex-direction: column; gap: 12px; text-align: left; }
}
</style>
